import { fetchDrmTableData } from "../../service/fetchDrmTableData";
import {
  setDrmTableDataFailure,
  setDrmTableDataSuccess,
} from "../reducers/drmTableDataSlice";
import { put, takeLatest, call, cancelled } from "redux-saga/effects";
import { ENUM_DATA } from "../../constData/enum";
import { convertToDDMMYYYY } from "../../helpers";

const mapCommonFields = (item) => ({
  id: item?.id ?? ENUM_DATA.Blank,
  sagaId: item?.sagaId ?? ENUM_DATA.Blank,
  carrierScac: item?.carrierScac ?? ENUM_DATA.Blank,
  carrierName: item?.carrierName ?? ENUM_DATA.Blank,
  serviceScac: item?.serviceScac ?? ENUM_DATA.Blank,
  tariffNumber: item?.tariffNumber ?? ENUM_DATA.Blank,
  tariffId: item?.tariffId ?? ENUM_DATA.Blank,
  tariffDetails: item?.tariffDetails ?? ENUM_DATA.Blank,
  equipmentType: item?.equipmentType ?? ENUM_DATA.Blank,
  laneDescription: item?.laneDescription ?? ENUM_DATA.Blank,
  originZone: item?.originZone ?? ENUM_DATA.Blank,
  originZoneDesc: item?.originZoneDesc ?? ENUM_DATA.Blank,
  originZoneShortDesc: item?.originZoneShortDesc ?? ENUM_DATA.Blank,
  originCity: item?.originCity ?? ENUM_DATA.Blank,
  originState: item?.originState ?? ENUM_DATA.Blank,
  originCountry: item?.originCountry ?? ENUM_DATA.Blank,
  originPostalCode: item?.originPostalCode ?? ENUM_DATA.Blank,
  destinationZone: item?.destinationZone ?? ENUM_DATA.Blank,
  destinationZoneDesc: item?.destinationZoneDesc ?? ENUM_DATA.Blank,
  destinationZoneShortDesc: item?.destinationZoneShortDesc ?? ENUM_DATA.Blank,
  destinationCity: item?.destinationCity ?? ENUM_DATA.Blank,
  destinationState: item?.destinationState ?? ENUM_DATA.Blank,
  destinationPostalCode: item?.destinationPostalCode ?? ENUM_DATA.Blank,
  destinationCountry: item?.destinationCountry ?? ENUM_DATA.Blank,
  lineHaulChargeType: item?.lineHaulChargeType ?? ENUM_DATA.Blank,
  effectiveDate: item?.effectiveDate ?? ENUM_DATA.Blank,
  expirationDate: item?.expirationDate ?? ENUM_DATA.Blank,
  awardBackup: item?.awardBackup ?? ENUM_DATA.Blank,
  qtyActiveFlag: item?.qtyActiveFlag ?? ENUM_DATA.Blank,
  minShipmentsAllocated: item?.minShipmentsAllocated === null ? ENUM_DATA.Blank : item.minShipmentsAllocated,
  maxShipmentsAllocated: item?.maxShipmentsAllocated === null ? ENUM_DATA.Blank : item.maxShipmentsAllocated,
  percentageActiveFlag: item?.percentageActiveFlag ?? ENUM_DATA.Blank,
  minCarrierAllocationPercentage: ENUM_DATA.Empty,
  maxCarrierAllocationPercentage: item?.maxCarrierAllocationPercentage == null ? ENUM_DATA.Blank : item.maxCarrierAllocationPercentage,
  modeOfTransport: item?.modeOfTransport ?? ENUM_DATA.Blank,
  status: item?.status ?? ENUM_DATA.Blank,
  message: item?.message ?? ENUM_DATA.Blank,
  region: item?.region ?? ENUM_DATA.Blank,
  carrierManager: item?.carrierManager ?? ENUM_DATA.Blank,
  primaryCustomers: item?.primaryCustomers ?? ENUM_DATA.Blank,
  versionNumber: item?.versionNumber ?? ENUM_DATA.Blank,
  errors: item?.errors?.length > 0 ? item.errors.map((error) => ({
    record: error.columnName ?? ENUM_DATA.Blank,
    errorMessage: error.errorMessage ?? ENUM_DATA.Blank
  })) : [],
  defaultError: item?.defaultError ?? ENUM_DATA.Blank,
  destHub: item?.destHub ?? ENUM_DATA.Blank,
  orgHub: item?.orgHub ?? ENUM_DATA.Blank,
  miles: item?.miles ?? ENUM_DATA.Blank,
  systemRateId: item?.systemRateId ?? ENUM_DATA.Blank
});

const mapLineHaulRate = (item) => {
  if (item?.lineHaulChargeType === "BAF") {
    return item?.baf ?? ENUM_DATA.Blank;
  }

  if (item?.lineHaulChargeType === "CIVA") {
    return item?.civa ?? ENUM_DATA.Blank;
  }

  if (item?.lineHaulChargeType === "ETS") {
    return item?.ets ?? ENUM_DATA.Blank;
  }

  return item?.lineHaulRate ?? ENUM_DATA.Blank;
};

const mapBidSpecificFields = (item) => ({
  itemType: item?.itemType ?? ENUM_DATA.Blank,
  item: item?.item ?? ENUM_DATA.Blank,
  estimatedMileagePcMiler: item?.estimatedMileagePcMiler ?? ENUM_DATA.Blank,
  annualShipmentVolume: item?.annualShipmentVolume ?? ENUM_DATA.Blank,
  availableAnnualShipmentVolume: item?.availableAnnualShipmentVolume ?? ENUM_DATA.Blank,
  annualMaxShipmentVolume: item?.annualMaxShipmentVolume ?? ENUM_DATA.Blank,
  bidRateWoFuel: item?.bidRateWoFuel ?? ENUM_DATA.Blank,
  laneServiceType: item?.laneServiceType ?? ENUM_DATA.Blank,
  incoterm: item?.incoterm ?? ENUM_DATA.Blank,
  supplier: item?.supplier ?? ENUM_DATA.Blank,
  transitTime: item?.transitTime ?? ENUM_DATA.Blank,
  originPortCountry: item?.originPortCountry ?? ENUM_DATA.Blank,
  originPort: item?.originPort ?? ENUM_DATA.Blank,
  destinationTransportationMode: item?.destinationTransportationMode ?? ENUM_DATA.Blank,
  tradeLane: item?.tradeLane ?? ENUM_DATA.Blank,
  destinationPort: item?.destinationPort ?? ENUM_DATA.Blank,
  destinationPortCountry: item?.destinationPortCountry ?? ENUM_DATA.Blank,
  lineHaulRate: mapLineHaulRate(item),
  bucketType: item?.bucketType ?? ENUM_DATA.Blank,
  baf: item?.baf ?? ENUM_DATA.Blank,
  civa: item?.civa ?? ENUM_DATA.Blank,
  ets: item?.ets ?? ENUM_DATA.Blank,
  qtyStartDate: item?.effectiveDate ? convertToDDMMYYYY(item.effectiveDate) : ENUM_DATA.Blank,
  qtyEndDate: item?.expirationDate ? convertToDDMMYYYY(item.expirationDate) : ENUM_DATA.Blank,
  pctStartDate: item?.effectiveDate ? convertToDDMMYYYY(item.effectiveDate) : ENUM_DATA.Blank,
  pctEndDate: item?.expirationDate ? convertToDDMMYYYY(item.expirationDate) : ENUM_DATA.Blank,
  changeComment: item?.changeComment ?? ENUM_DATA.Blank,
  coupaFile: item?.coupaFile ? {
    id: item.coupaFile.id ?? ENUM_DATA.Blank,
    coupaFileName: item.coupaFile.coupaFileName ?? ENUM_DATA.Blank,
    status: item?.status ?? ENUM_DATA.Blank,
    coupaFileType: item.coupaFile.coupaFileType ?? ENUM_DATA.Blank,
    domesticCoupaFileData: null
  } : ENUM_DATA.Blank,
  bidFileName: item?.coupaFile?.coupaFileName ?? ENUM_DATA.Blank,
  // coupaFileRecordErrors: item?.coupaFileRecordErrors?.length > 0 ? item.coupaFileRecordErrors.map((error) => ({
  //   record: error.columnName ?? ENUM_DATA.Blank,
  //   errorMessage: error.errorMessage ?? ENUM_DATA.Blank
  // })) : [],
});

const mapDailySpecificFields = (item) => ({
  rangeCode: item?.rangeCode ?? ENUM_DATA.Blank,
  rangeToUnits: item?.rangeToUnits ?? ENUM_DATA.Blank,
  rate: item?.rate ?? ENUM_DATA.Blank,
  qtyBucketType: item?.qtyBucketType ?? ENUM_DATA.Blank,
  pctBucketType: item?.pctBucketType ?? ENUM_DATA.Blank,
  qtyStartDate: item?.qtyStartDate ?? ENUM_DATA.Blank,
  qtyEndDate: item?.qtyEndDate ?? ENUM_DATA.Blank,
  pctStartDate: item?.pctStartDate ?? ENUM_DATA.Blank,
  pctEndDate: item?.pctEndDate ?? ENUM_DATA.Blank,
  orderType: item?.orderType ?? ENUM_DATA.Blank,
  preAwarded: item?.preAwarded ?? ENUM_DATA.Blank,
  btfRate: item?.btfRate ?? ENUM_DATA.Blank,
  changeComment: item?.changeComment ?? ENUM_DATA.Blank,
});

const parseTableData = (data, tableName) => {
  if (!data?.details?.data?.length) {
    return [];
  }

  return data.details.data.map((item) => {
    const commonFields = mapCommonFields(item);
    
    if (tableName === "drmBidTableData") {
      return {
        ...commonFields,
        ...mapBidSpecificFields(item),
      };
    } else if (tableName === "drmDailyTableData") {
      return {
        ...commonFields,
        ...mapDailySpecificFields(item),
      };
    }
    
    return commonFields;
  });
};

export function* workerGetDrmTableDataFetch({ payload }) {
  const controller = new AbortController();
  try {
    const response = yield call(fetchDrmTableData, [
      payload.pageNum,
      payload.reqBody,
      payload.pageSize,
      payload.tableName,
      controller,
    ]);

    const data = response.data;
    const parsedShipmentValues = parseTableData(data, payload.tableName);

    const rest =
      payload.tableName === "drmBidTableData" ||
      payload.tableName === "drmDailyTableData"
        ? {
            onPage: data?.details?.onPage,
            pageSize: data?.details?.pageSize,
            totalPages: data?.details?.totalPages,
            totalRecords: data?.details?.totalRecords,
          }
        : { ...data };

    yield put(
      setDrmTableDataSuccess({
        ...rest,
        drmMaintenanceDetails: parsedShipmentValues,
        tableType: payload.tableName,
      })
    );
  } catch (error) {
    yield put(
      setDrmTableDataFailure({
        error: error.toString(),
        tableType: payload.tableName,
      })
    );
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

function* drmTableDataSaga() {
  yield takeLatest("drmTableData/getDrmTableData", workerGetDrmTableDataFetch);
}

export default drmTableDataSaga;